// NotificationBell — student sidebar notification bell | shows unread badge + dropdown list of counselor feedback alerts | marks read on open | clicking navigates to relevant page

import { useEffect, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useStudentNotifications } from '../../hooks/useStudentNotifications'
import type { StudentNotification } from '../../services/notificationsApi'

interface NotificationBellProps {
  collapsed?: boolean
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function notificationIcon(type: string): string {
  if (type === 'deadline_alert') return '📅'
  if (type.includes('essay') || type.includes('draft_comparison')) return '📝'
  if (type.includes('activity')) return '🏃'
  return '🔔'
}

function getNotificationRoute(n: StudentNotification): string {
  const { notification_type, entity_type, entity_id } = n
  if (notification_type === 'deadline_alert') return '/student/chat?thread=ai'
  if (entity_type === 'essay' && entity_id) return `/student/essays/${entity_id}`
  if (entity_type === 'supplement') return '/student/supplements'
  if (entity_type === 'activity' || entity_type === 'activities') return '/student/activities'
  return '/student/chat'
}

function NotificationItem({
  notification,
  onRead,
  onNavigate,
}: {
  notification: StudentNotification
  onRead: (id: string) => void
  onNavigate: (route: string) => void
}) {
  const handleClick = () => {
    if (!notification.is_read) onRead(notification.id)
    onNavigate(getNotificationRoute(notification))
  }

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left px-4 py-3 flex gap-3 items-start transition-colors duration-150 hover:bg-violet-500/10 ${
        notification.is_read ? 'opacity-50' : ''
      }`}
    >
      <span className="text-base flex-shrink-0 mt-0.5">
        {notificationIcon(notification.notification_type)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-200 leading-snug line-clamp-2">
          {notification.message ?? 'New feedback from your counselor'}
        </p>
        <p className="text-[10px] text-gray-500 mt-1">
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>
      {!notification.is_read && (
        <span className="w-2 h-2 rounded-full bg-violet-400 flex-shrink-0 mt-1.5" />
      )}
    </button>
  )
}

function NotificationBell({ collapsed = false }: NotificationBellProps) {
  const { notifications, unreadCount, loading, markRead, markAllRead } =
    useStudentNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const handleNavigate = (route: string) => {
    setOpen(false)
    navigate(route)
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Mark all read when dropdown opens
  const handleOpen = () => {
    setOpen((v) => !v)
  }

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={handleOpen}
        className={`relative w-full flex items-center ${
          collapsed ? 'justify-center' : 'gap-3'
        } px-4 py-3 rounded-[24px] text-sm font-medium border transition-all duration-150 bg-white text-gray-700 border-gray-200 dark:bg-[#050309] dark:border-[#3f3277] dark:text-[#d6cfff] hover:bg-[#ede9fe] dark:hover:bg-[#130b33] hover:border-gray-300 dark:hover:border-[#b193ff] hover:text-gray-900 dark:hover:text-white`}
        aria-label="Notifications"
        title={collapsed ? 'Notifications' : undefined}
      >
        <div className="relative flex-shrink-0">
          <Bell className="h-4 w-4 opacity-90" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full bg-violet-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="whitespace-nowrap overflow-hidden"
            >
              Notifications
            </motion.span>
          )}
        </AnimatePresence>
        {collapsed && (
          <span className="absolute left-full ml-3 px-3 py-1.5 rounded-lg bg-black/80 border border-white/10 text-xs text-white opacity-0 group-hover:opacity-100 pointer-events-none shadow-xl whitespace-nowrap">
            Notifications
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute z-50 bottom-full mb-2 w-80 rounded-2xl border border-white/10 bg-[#0e0920]/95 backdrop-blur-xl shadow-2xl overflow-hidden ${
              collapsed ? 'left-full ml-3 bottom-0' : 'left-0'
            }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
              <span className="text-sm font-semibold text-white">Notifications</span>
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-violet-400 hover:text-violet-300 transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
              {loading && (
                <p className="px-4 py-6 text-xs text-gray-500 text-center">Loading…</p>
              )}
              {!loading && notifications.length === 0 && (
                <p className="px-4 py-6 text-xs text-gray-500 text-center">
                  No notifications yet
                </p>
              )}
              {!loading &&
                notifications.map((n) => (
                  <NotificationItem key={n.id} notification={n} onRead={markRead} onNavigate={handleNavigate} />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default NotificationBell
